import type { RhythmChallenge } from '../data/rhythmChallenges';
import type { RhythmPattern } from './rhythmPattern';
import { cloneRhythmPattern } from './rhythmCompare';

const STORAGE_KEY = 'boom-custom-rhythms';

export interface CustomRhythmChallenge extends RhythmChallenge {
  custom: true;
  createdAt: number;
}

function loadRaw(): CustomRhythmChallenge[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomRhythmChallenge[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(list: CustomRhythmChallenge[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function loadCustomRhythms(): CustomRhythmChallenge[] {
  return loadRaw().sort((a, b) => b.createdAt - a.createdAt);
}

export function saveCustomRhythm(
  input: Omit<RhythmChallenge, 'id'> & { id?: string },
): CustomRhythmChallenge {
  const list = loadRaw();
  const id = input.id ?? `custom-${Date.now()}`;
  const existing = list.findIndex((c) => c.id === id);
  const entry: CustomRhythmChallenge = {
    id,
    title: input.title.trim() || 'Mi ritmo',
    description: input.description.trim() || 'Ritmo creado por el profesor',
    bpm: input.bpm,
    pattern: cloneRhythmPattern(input.pattern),
    custom: true,
    createdAt: existing >= 0 ? list[existing].createdAt : Date.now(),
  };
  if (existing >= 0) {
    list[existing] = entry;
  } else {
    list.unshift(entry);
  }
  persist(list);
  return entry;
}

export function deleteCustomRhythm(id: string): void {
  persist(loadRaw().filter((c) => c.id !== id));
}

export function patternHasHits(pattern: RhythmPattern): boolean {
  return pattern.measures.some((m) =>
    m.slots.some((s) => s.active && s.cells.some((c) => c.hit)),
  );
}
